import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, X } from 'lucide-react'
import { Button } from '../ds/index'
import { Icon } from './Icon'
import { HandpickedTree } from './BrandMark'
import { api, post } from '../lib/api'
import { useSession } from '../lib/session'

type MomentsData = {
  welcome_back?: { first_name: string; first_time?: boolean }
  first_lead?: { id: string; summary: string | null }
  recap?: { period: string; month: string; recommendations: number; contacted: number; groups: number }
  aplus?: { id: string; summary: string | null; tier: string }[]
}
type Moment = { kind: 'welcome_back' | 'first_lead' | 'recap' | 'aplus'; data: MomentsData }

const reduced = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

/**
 * The small animated moments, one at a time and each once (lib/routes/moments.js):
 * welcome back (at most every 12 hours), a client's first lead, their monthly
 * recap, a rep's A+ lead. Handpicked's brand only. Mounted once per shell.
 */
export function Moments() {
  const { config } = useSession()
  const [queue, setQueue] = useState<Moment[]>([])
  const on = config?.brand === 'handpicked'

  useEffect(() => {
    if (!on) return
    api<MomentsData>('/api/moments').then((d) => {
      const q: Moment[] = []
      if (d.welcome_back) q.push({ kind: 'welcome_back', data: d })
      if (d.first_lead) q.push({ kind: 'first_lead', data: d })
      else if (d.recap) q.push({ kind: 'recap', data: d })
      if (d.aplus?.length) q.push({ kind: 'aplus', data: d })
      setQueue(q)
    }).catch(() => { /* decoration: never in the way */ })
  }, [on])

  const next = () => setQueue((q) => q.slice(1))
  const m = queue[0]
  if (!m) return null
  if (m.kind === 'welcome_back') return <WelcomeBack name={m.data.welcome_back!.first_name} firstTime={Boolean(m.data.welcome_back!.first_time)} onDone={next} />
  if (m.kind === 'first_lead') return <FirstLead lead={m.data.first_lead!} onDone={next} />
  if (m.kind === 'recap') return <Recap recap={m.data.recap!} onDone={next} />
  return <APlus leads={m.data.aplus!} onDone={next} />
}

/** The tree in a gold ring that draws itself: the brand mark every moment shares. */
function RingTree({ size }: { size: number }) {
  return (
    <span className="mo-ringtree" style={{ width: size, height: size }}>
      <svg viewBox="0 0 200 200" aria-hidden="true"><circle cx="100" cy="100" r="94" /></svg>
      <span className="mo-tree"><HandpickedTree size={Math.round(size * 0.8)} /></span>
    </span>
  )
}

/** Leaves drifting down, as on the first welcome. Fixed per mount so a re-render never reshuffles them. */
function Leaves({ count = 12, from = 0.2 }: { count?: number; from?: number }) {
  const leaves = useMemo(() => Array.from({ length: count }, (_, i) => ({
    left: 4 + ((i * 53) % 92), delay: from + ((i * 37) % 50) / 10, dur: 8 + ((i * 29) % 50) / 10,
    size: 9 + ((i * 17) % 11), sway: 24 + ((i * 41) % 40), gold: i % 4 === 1, turn: (i * 67) % 360,
  })), [count, from])
  return (
    <div className="ob-leaves" aria-hidden="true">
      {leaves.map((l, i) => (
        <span key={i} className={`ob-leaf${l.gold ? ' is-gold' : ''}`}
              style={{ left: `${l.left}%`, width: l.size, height: l.size * 1.7, animationDelay: `${l.delay}s, ${l.delay}s`, animationDuration: `${l.dur}s, ${l.dur / 3}s`,
                       ['--sway' as string]: `${l.sway}px`, ['--turn' as string]: `${l.turn}deg` }} />
      ))}
    </div>
  )
}

/**
 * Welcome back: about a second and a half, then it fades on its own. A tap
 * dismisses it at once; it never waits on a button.
 */
function WelcomeBack({ name, firstTime, onDone }: { name: string; firstTime: boolean; onDone: () => void }) {
  const [leaving, setLeaving] = useState(false)
  useEffect(() => {
    const hold = reduced() ? 900 : 1500
    const t1 = setTimeout(() => setLeaving(true), hold)
    const t2 = setTimeout(onDone, hold + 380)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const word = firstTime ? 'Welcome' : 'Welcome back'
  const greeting = name ? `${word}, ${name}` : word
  return (
    <div className={`mo-welcome${leaving ? ' is-leaving' : ''}`} role="status" aria-live="polite" onClick={() => { setLeaving(true); setTimeout(onDone, 200) }}>
      <div className="mo-welcome-inner">
        <RingTree size={112} />
        <p className="mo-welcome-text">{greeting}</p>
        <span className="mo-rule" aria-hidden="true" />
      </div>
    </div>
  )
}

/** A client's very first lead: the one moment that proves what they pay for. */
function FirstLead({ lead, onDone }: { lead: { id: string; summary: string | null }; onDone: () => void }) {
  const nav = useNavigate()
  const seen = () => post('/api/moments/seen', { kind: 'first_lead' }).catch(() => {})
  const close = () => { void seen(); onDone() }
  const open = () => { void seen(); onDone(); nav(`/portal/leads/${lead.id}`) }
  return (
    <div className="mo-backdrop" role="dialog" aria-modal="true" aria-labelledby="mo-first-title">
      <Leaves count={14} from={0.6} />
      <div className="mo-card">
        <button type="button" className="mo-close" onClick={close} aria-label="Close"><Icon of={X} size={18} /></button>
        <RingTree size={128} />
        <p className="mo-kicker">A first for you</p>
        <h2 id="mo-first-title" className="mo-title">Your first Handpicked lead</h2>
        {lead.summary && <p className="mo-quote">{lead.summary}</p>}
        <p className="mo-sub">A rep recommended you to someone who asked for help. This is how every lead will reach you.</p>
        <div className="mo-actions">
          <Button onClick={open}>See your lead <Icon of={ArrowRight} size={16} /></Button>
          <Button variant="ghost" onClick={close}>Later</Button>
        </div>
      </div>
    </div>
  )
}

/** Counts from zero to the number over about a second; the number itself with reduced motion. */
function CountUp({ to, delay = 0 }: { to: number; delay?: number }) {
  const [n, setN] = useState(reduced() ? to : 0)
  useEffect(() => {
    if (reduced()) return
    let raf = 0
    const start = performance.now() + delay
    const dur = 1100
    const tick = (now: number) => {
      const t = Math.min(1, Math.max(0, (now - start) / dur))
      setN(Math.round(to * (1 - Math.pow(1 - t, 3))))
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [to, delay])
  return <>{n.toLocaleString('en-US')}</>
}

/** Once a month: last month at a glance, matching the summary email. */
function Recap({ recap, onDone }: { recap: NonNullable<MomentsData['recap']>; onDone: () => void }) {
  const nav = useNavigate()
  const seen = () => post('/api/moments/seen', { kind: 'recap', id: recap.period }).catch(() => {})
  const close = () => { void seen(); onDone() }
  const tiles = [
    { n: recap.recommendations, label: recap.recommendations === 1 ? 'recommendation' : 'recommendations' },
    { n: recap.groups, label: recap.groups === 1 ? 'group' : 'groups' },
    { n: recap.contacted, label: 'reached out' },
  ]
  return (
    <div className="mo-backdrop" role="dialog" aria-modal="true" aria-labelledby="mo-recap-title">
      <div className="mo-card">
        <button type="button" className="mo-close" onClick={close} aria-label="Close"><Icon of={X} size={18} /></button>
        <RingTree size={84} />
        <p className="mo-kicker">Your month with Handpicked</p>
        <h2 id="mo-recap-title" className="mo-title">{recap.month} in review</h2>
        <div className="mo-tiles">
          {tiles.map((t, i) => (
            <div key={t.label} className="mo-tile" style={{ animationDelay: `${0.35 + i * 0.12}s` }}>
              <strong><CountUp to={t.n} delay={450 + i * 120} /></strong><span>{t.label}</span>
            </div>
          ))}
        </div>
        <div className="mo-actions">
          <Button onClick={() => { void seen(); onDone(); nav('/portal/leads') }}>See every lead <Icon of={ArrowRight} size={16} /></Button>
          <Button variant="ghost" onClick={close}>Close</Button>
        </div>
      </div>
    </div>
  )
}

/**
 * A rep's lead was graded at the top tier: a gold card slides in at the corner
 * with one leaf drifting across it, then leaves on its own. Never blocks work.
 */
function APlus({ leads, onDone }: { leads: NonNullable<MomentsData['aplus']>; onDone: () => void }) {
  const nav = useNavigate()
  const [leaving, setLeaving] = useState(false)
  const first = leads[0]
  const seenAll = () => Promise.all(leads.map((l) => post('/api/moments/seen', { kind: 'aplus', id: l.id }).catch(() => {})))
  const close = () => { void seenAll(); setLeaving(true); setTimeout(onDone, 260) }
  useEffect(() => {
    const t = setTimeout(close, 8000)
    return () => clearTimeout(t)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className={`mo-toast${leaving ? ' is-leaving' : ''}`} role="status" aria-live="polite">
      <span className="mo-toast-leaf" aria-hidden="true" />
      <button type="button" className="mo-close" onClick={close} aria-label="Close"><Icon of={X} size={16} /></button>
      <span className="mo-badge">{first.tier}</span>
      <div className="mo-toast-body">
        <strong>{leads.length > 1 ? `${leads.length} of your leads were graded ${first.tier}` : `Your lead was graded ${first.tier}`}</strong>
        {first.summary && <span className="muted">{first.summary}</span>}
        <span className="mo-toast-note">Top tier. Nice work.</span>
      </div>
      <button type="button" className="link-button mo-toast-link" onClick={() => { void seenAll(); onDone(); nav(`/my/${first.id}`) }}>Open</button>
    </div>
  )
}
